import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static values = {
    messagesPath: String,
    profilePath: String,
    servicesPath: String,
    rootPath: String,
    threshold: { type: Number, default: 80 }
  }

  connect() {
    this.touchStartX = 0
    this.touchStartY = 0
    this.currentTranslateX = 0
    this.isSwiping = false
    this.isDragging = false
    
    if (this.isMobile()) {
      this.bindEvents()
      this.setupTurboHooks()
      console.log("Swipe Navigation: Visual Dragging Enabled")
    }
  }

  setupTurboHooks() {
    document.addEventListener("turbo:before-render", () => {
      document.body.style.transform = ''
      document.body.style.transition = ''
    })
  }

  disconnect() {
    this.unbindEvents()
    this.resetStyles()
  }

  isMobile() {
    return window.matchMedia("(max-width: 768px)").matches
  }

  bindEvents() {
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    
    document.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    document.addEventListener('touchend', this.handleTouchEnd, { passive: true })
  }

  unbindEvents() {
    document.removeEventListener('touchstart', this.handleTouchStart)
    document.removeEventListener('touchmove', this.handleTouchMove)
    document.removeEventListener('touchend', this.handleTouchEnd)
  }

  handleTouchStart(e) {
    if (
      e.target.closest('.swipe-ignore') ||
      e.target.closest('.overflow-x-auto') ||
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('select') ||
      e.target.closest('form') ||
      e.target.closest('input') ||
      e.target.closest('textarea')
    ) {
      this.ignoreSwipe = true
      return
    }

    this.ignoreSwipe = false
    this.touchStartX = e.touches[0].clientX
    this.touchStartY = e.touches[0].clientY
    this.isSwiping = false
    this.isDragging = false
    
    // Reset transitions
    document.body.style.transition = 'none'
  }

  handleTouchMove(e) {
    if (this.ignoreSwipe || this.isSwiping) return

    const touchX = e.touches[0].clientX
    const touchY = e.touches[0].clientY
    const deltaX = touchX - this.touchStartX
    const deltaY = Math.abs(touchY - this.touchStartY)

    // Only start dragging if horizontal movement is dominant
    if (!this.isDragging && Math.abs(deltaX) > 10 && deltaY < 10) {
      this.isDragging = true
    }

    if (this.isDragging) {
      // Prevent vertical scrolling while dragging horizontally
      if (e.cancelable) e.preventDefault()
      
      this.currentTranslateX = touchX - this.touchStartX
      
      // Apply translation to the body
      const resistance = 0.8 
      const translateX = this.currentTranslateX * resistance
      
      document.body.style.transform = `translateX(${translateX}px)`
      document.body.style.overflowX = 'hidden'
      
      // Add shadow if dragging right (revealing back)
      if (translateX > 0) {
        document.body.style.boxShadow = '-10px 0 30px rgba(0,0,0,0.1)'
      } else {
        document.body.style.boxShadow = '10px 0 30px rgba(0,0,0,0.1)'
      }
    }
  }

  handleTouchEnd(e) {
    if (this.ignoreSwipe || this.isSwiping) return

    const deltaX = this.currentTranslateX
    const absDeltaX = Math.abs(deltaX)
    
    this.isDragging = false
    document.body.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s'

    if (absDeltaX > this.thresholdValue) {
      // Threshold met, trigger navigation
      this.isSwiping = true
      const path = window.location.pathname

      if (deltaX < 0) {
        // Dragging Left (Finger R -> L)
        document.body.style.transform = 'translateX(-100%)'
        setTimeout(() => this.swipeLeft(path), 150)
      } else {
        // Dragging Right (Finger L -> R)
        document.body.style.transform = 'translateX(100%)'
        setTimeout(() => this.swipeRight(path), 150)
      }
    } else {
      // Snap back
      this.resetStyles()
    }
  }

  resetStyles() {
    document.body.style.transform = ''
    document.body.style.boxShadow = ''
    document.body.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s'
    setTimeout(() => {
      document.body.style.overflowX = ''
      document.body.style.transition = ''
    }, 300)
  }

  // Finger R -> L: Move to Messages side
  swipeLeft(path) {
    console.log("Swipe Left detected on:", path)
    if (this.isPath(path, this.profilePathValue)) {
      this.visit(this.rootPathValue)
    } else {
      this.visit(this.messagesPathValue || this.servicesPathValue)
    }
  }

  // Finger L -> R: Move to Profile side or Smart Back
  swipeRight(path) {
    console.log("Swipe Right detected on:", path)
    if (this.isPath(path, this.rootPathValue)) {
      if (this.profilePathValue) {
        this.visit(this.profilePathValue)
      } else {
        this.visit(this.servicesPathValue)
      }
    } else {
      // SMART BACK: Try to go back, but clean up messy URLs
      console.log("Smart Back: Navigating back")
      window.history.back()
      
      // Safety: If we get stuck on a long filter URL, force a clean Home visit
      // Only do this if we are not on the search pages where long URLs are expected
      setTimeout(() => {
        const isSearchPage = window.location.pathname.includes('/services') || window.location.pathname.includes('/classes') || window.location.pathname.includes('/rooms')
        if (window.location.search.length > 100 && !isSearchPage) {
          console.log("Detected messy URL in history, forcing clean Home.")
          this.visit(this.rootPathValue)
        }
      }, 100)
    }
  }

  isPath(current, target) {
    if (!target) return false
    const cleanCurrent = current.split('?')[0].replace(/\/$/, "") || "/"
    const cleanTarget = target.split('?')[0].replace(/\/$/, "") || "/"
    return cleanCurrent === cleanTarget
  }

  visit(url) {
    if (url && window.location.pathname !== url) {
      console.log("Explicit Visit:", url)
      Turbo.visit(url)
    }
  }
}
